import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Plus, FolderOpen, Clock, Edit, Archive, RotateCcw, Star, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';
import BudgetAlertBadge from '../components/BudgetAlertBadge';
import ProjectBudgetForm from '../components/ProjectBudgetForm';
import ProjectTimelineForm from '../components/ProjectTimelineForm';
import DeadlineAlertBadge from '../components/DeadlineAlertBadge';
import ProjectTagsInput from '../components/ProjectTagsInput';
import ProjectCategorySelect from '../components/ProjectCategorySelect';
import ProjectCard from '../components/ProjectCard';
import { AnimatedLogoRolodex } from '../components/ui/animated-logo-rolodex';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const PRESET_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Orange
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#F97316', // Orange
  '#14B8A6', // Teal
  '#A855F7', // Violet
];

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [archivedProjects, setArchivedProjects] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectForm, setProjectForm] = useState({ 
    name: '', 
    color: PRESET_COLORS[0], 
    is_billable: false,
    budget: null,
    hourly_rate: null,
    timeline: null,
    milestones: null,
    status: 'not_started',
    tags: [],
    category: null,
    is_default: false
  });
  const [projectBudgets, setProjectBudgets] = useState({});
  const [projectTimelines, setProjectTimelines] = useState({});
  const [showBudgetOnly, setShowBudgetOnly] = useState(false);
  const [timelineFocus, setTimelineFocus] = useState(false);
  const [budgetFocus, setBudgetFocus] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchProjects();
    fetchArchivedProjects();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editId = params.get('edit');
    if (!editId) {
      return;
    }

    const focus = params.get('focus');
    const combined = [...projects, ...archivedProjects];
    const project = combined.find((p) => p.id === editId);
    if (!project) {
      return;
    }

    if (focus === 'timeline') {
      openTimelineEditor(project);
    } else if (focus === 'budget') {
      openBudgetEditor(project);
    } else {
      setTimelineFocus(false);
      setBudgetFocus(false);
      populateProjectForm(project);
      setModalOpen(true);
    }

    navigate(location.pathname, { replace: true });
  }, [projects, archivedProjects, location.search, location.pathname, navigate]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
      
      // Fetch budget status for projects with budgets
      const budgetPromises = response.data
        .filter(p => p.budget)
        .map(p => 
          axios.get(`${API}/projects/${p.id}/budget`)
            .then(res => ({ projectId: p.id, data: res.data }))
            .catch(err => {
              console.error(`Failed to fetch budget for ${p.id}:`, err);
              return null;
            })
        );
      
      const budgetResults = await Promise.all(budgetPromises);
      const budgetsMap = {};
      budgetResults.filter(Boolean).forEach(({ projectId, data }) => {
        budgetsMap[projectId] = data;
      });
      setProjectBudgets(budgetsMap);
      
      // Fetch timeline status for projects with timelines
      const timelinePromises = response.data
        .filter(p => p.timeline)
        .map(p => 
          axios.get(`${API}/projects/${p.id}/timeline`)
            .then(res => ({ projectId: p.id, data: res.data }))
            .catch(err => {
              console.error(`Failed to fetch timeline for ${p.id}:`, err);
              return null;
            })
        );
      
      const timelineResults = await Promise.all(timelinePromises);
      const timelinesMap = {};
      timelineResults.filter(Boolean).forEach(({ projectId, data }) => {
        timelinesMap[projectId] = data;
      });
      setProjectTimelines(timelinesMap);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects/archived`);
      console.log('📦 Archived projects fetched:', response.data);
      console.log('📦 Archived count:', response.data.length);
      setArchivedProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch archived projects:', error);
    }
  };

  const handleArchiveProject = async (e, projectId) => {
    e.stopPropagation();
    if (!window.confirm('Archive this project? It will be hidden from the main list.')) return;
    
    try {
      await axios.post(`${API}/projects/${projectId}/archive`, null, {
        params: { reason: 'completed' }
      });
      toast.success('Project archived');
      fetchProjects();
      fetchArchivedProjects();
    } catch (error) {
      toast.error('Failed to archive project');
    }
  };

  const handleRestoreProject = async (e, projectId) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/projects/${projectId}/restore`);
      toast.success('Project restored');
      fetchProjects();
      fetchArchivedProjects();
    } catch (error) {
      toast.error('Failed to restore project');
    }
  };

  const handleDeleteProject = async (e, projectId, projectName) => {
    e.stopPropagation();
    
    // Strong confirmation for permanent deletion
    const confirmed = window.confirm(
      `⚠️ WARNING: This will permanently delete "${projectName}" and cannot be undone!\n\n` +
      `All project data including time entries, notes, and budgets will be lost.\n\n` +
      `Are you absolutely sure you want to delete this project?`
    );
    
    if (!confirmed) return;
    
    // Double confirmation
    const doubleConfirmed = window.confirm(
      `This is your last chance. Delete "${projectName}" permanently?`
    );
    
    if (!doubleConfirmed) return;
    
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      toast.success('Project deleted permanently');
      fetchProjects();
      fetchArchivedProjects();
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  const handleToggleDefault = async (e, projectId, isCurrentlyDefault) => {
    e.stopPropagation();
    try {
      if (isCurrentlyDefault) {
        await axios.delete(`${API}/projects/${projectId}/unset-default`);
        toast.success('Default status removed');
      } else {
        await axios.post(`${API}/projects/${projectId}/set-default`);
        toast.success('Project set as default');
      }
      fetchProjects();
    } catch (error) {
      toast.error('Failed to update default status');
    }
  };

  const populateProjectForm = (project) => {
    setEditingProject(project);
    setProjectForm({
      name: project.name,
      color: project.color,
      is_billable: project.is_billable,
      budget: project.budget,
      hourly_rate: project.hourly_rate,
      timeline: project.timeline,
      milestones: project.milestones,
      status: project.status || 'not_started',
      tags: project.tags || [],
      category: project.category || null,
      is_default: project.is_default || false
    });
  };

  const handleCreateProject = async () => {
    if (!projectForm.name.trim()) {
      toast.error('Please enter a project name');
      return;
    }

    try {
      if (editingProject) {
        // Update existing project
        await axios.put(`${API}/projects/${editingProject.id}`, projectForm);
        toast.success('Project updated!');
      } else {
        // Create new project
        await axios.post(`${API}/projects`, projectForm);
        toast.success('Project created!');
      }
      
      setModalOpen(false);
      setEditingProject(null);
      setProjectForm({ 
        name: '', 
        color: PRESET_COLORS[0], 
        is_billable: false,
        budget: null,
        hourly_rate: null,
        timeline: null,
        milestones: null,
        status: 'not_started',
        tags: [],
        category: null,
        is_default: false
      });
      setTimelineFocus(false);
      setBudgetFocus(false);
      fetchProjects();
      fetchArchivedProjects();
    } catch (error) {
      toast.error(editingProject ? 'Failed to update project' : 'Failed to create project');
    }
  };

  const handleEditProject = (e, project) => {
    e.stopPropagation(); // Prevent card click navigation
    setTimelineFocus(false);
    setBudgetFocus(false);
    populateProjectForm(project);
    setModalOpen(true);
  };

  const openTimelineEditor = (project) => {
    setTimelineFocus(true);
    setBudgetFocus(false);
    populateProjectForm(project);
    setModalOpen(true);
  };

  const openBudgetEditor = (project) => {
    setBudgetFocus(true);
    setTimelineFocus(false);
    populateProjectForm(project);
    setModalOpen(true);
  };

  const handleOpenCreateModal = () => {
    setEditingProject(null);
    setTimelineFocus(false);
    setBudgetFocus(false);
    setProjectForm({ 
      name: '', 
      color: PRESET_COLORS[0], 
      is_billable: false,
      budget: null,
      hourly_rate: null,
      timeline: null,
      milestones: null,
      status: 'not_started',
      tags: [],
      category: null,
      is_default: false
    });
    setModalOpen(true);
  };

  const handleBudgetChange = ({ budget, hourlyRate }) => {
    setProjectForm({
      ...projectForm,
      budget,
      hourly_rate: hourlyRate
    });
  };

  const handleTimelineChange = ({ timeline, milestones }) => {
    setProjectForm({
      ...projectForm,
      timeline,
      milestones
    });
  };

  const mapProjectToCard = (project, isArchivedView = false) => {
    const budgetStatus = projectBudgets[project.id];
    const timelineStatus = projectTimelines[project.id];

    const combinedBudget = budgetStatus?.budget ?? project.budget ?? null;
    const combinedSpent = budgetStatus?.spent ?? project.spent ?? {
      time_hours: 0,
      money_amount: 0
    };

    const combinedHourlyRate = project.hourly_rate || budgetStatus?.hourly_rate || null;

    const alerts = [];
    const hasTimeAlert = budgetStatus?.alerts?.time && budgetStatus.alerts.time.level !== 'none';
    const hasMoneyAlert = budgetStatus?.alerts?.money && budgetStatus.alerts.money.level !== 'none';

    if (hasTimeAlert) {
      alerts.push(
        <BudgetAlertBadge key="time-alert" alert={budgetStatus.alerts.time} compact />
      );
    }

    if (hasMoneyAlert) {
      alerts.push(
        <BudgetAlertBadge key="money-alert" alert={budgetStatus.alerts.money} compact />
      );
    }

    const hasDeadlineAlert = timelineStatus?.deadline_alert && (
      timelineStatus.deadline_alert.level !== 'none' || timelineStatus.deadline_alert.is_overdue
    );

    if (hasDeadlineAlert) {
      alerts.push(
        <DeadlineAlertBadge key="deadline-alert" alert={timelineStatus.deadline_alert} compact />
      );
    }

    const cardProject = {
      ...project,
      budget: combinedBudget,
      spent: combinedSpent,
      hourly_rate: combinedHourlyRate,
      alerts,
      status: project.status,
      timeline: project.timeline ?? timelineStatus?.timeline ?? null,
      earnings_this_week: budgetStatus?.earnings ?? project.earnings_this_week ?? 0,
      hours_this_week: project.hours_this_week ?? 0
    };

    const footer = (
      <>
        {budgetStatus?.earnings > 0 && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-lg px-3 py-2 mb-3 transition-colors">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-green-700 dark:text-green-300 transition-colors">Total Earnings:</span>
              <span className="text-sm font-bold text-green-800 dark:text-green-200 transition-colors">
                {(combinedHourlyRate?.currency || 'USD')} {budgetStatus.earnings.toFixed(2)}
              </span>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center text-gray-600 dark:text-gray-400 text-sm transition-colors">
            <Clock className="w-4 h-4 mr-2" />
            <span>View details →</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            {!isArchivedView && (
              <Button
                onClick={(e) => handleToggleDefault(e, project.id, project.is_default)}
                variant="outline"
                size="sm"
                title={project.is_default ? "Remove as default" : "Set as default"}
                className="min-w-[40px] h-9 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <Star className={`w-4 h-4 ${project.is_default ? 'fill-yellow-500 text-yellow-500 dark:fill-yellow-400 dark:text-yellow-400' : ''}`} />
              </Button>
            )}
            {isArchivedView ? (
              <>
                <Button
                  onClick={(e) => handleRestoreProject(e, project.id)}
                  variant="outline"
                  size="sm"
                  className="min-w-[90px] h-9 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Restore
                </Button>
                <Button
                  onClick={(e) => handleDeleteProject(e, project.id, project.name)}
                  variant="outline"
                  size="sm"
                  className="min-w-[85px] h-9 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 border-red-200 dark:border-red-800 transition-colors"
                  title="Delete permanently"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={(e) => handleArchiveProject(e, project.id)}
                  variant="outline"
                  size="sm"
                  className="min-w-[90px] h-9 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <Archive className="w-4 h-4 mr-1" />
                  Archive
                </Button>
                <Button
                  onClick={(e) => handleDeleteProject(e, project.id, project.name)}
                  variant="outline"
                  size="sm"
                  className="min-w-[85px] h-9 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 border-red-200 dark:border-red-800 transition-colors"
                  title="Delete permanently"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </>
            )}
            <Button
              onClick={(e) => handleEditProject(e, project)}
              variant="outline"
              size="sm"
              className="min-w-[75px] h-9 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <Edit className="w-4 h-4 mr-1" />
              Edit
            </Button>
          </div>
        </div>
      </>
    );

    return (
      <ProjectCard
        key={project.id}
        project={cardProject}
        onClick={() => navigate(`/projects/${project.id}`)}
        onDeadlineAction={() => openTimelineEditor(cardProject)}
        onBudgetAction={() => openBudgetEditor(cardProject)}
        footer={footer}
      />
    );
  };

  const filteredProjects = useMemo(() => {
    const sourceProjects = showArchived ? archivedProjects : projects;
    const hasBudget = (project) => {
      const budgetStatus = projectBudgets[project.id];
      const budget = budgetStatus?.budget ?? project.budget;
      return Boolean(
        budget &&
        (Number(budget.time_hours) > 0 || Number(budget.money_amount) > 0)
      );
    };

    return sourceProjects.filter((project) => {
      if (!showBudgetOnly) return true;
      return hasBudget(project);
    });
  }, [archivedProjects, projects, projectBudgets, showArchived, showBudgetOnly]);

  return (
    <div className="min-h-screen pb-24 bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-slate-900 dark:to-slate-800 transition-colors">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-md border-b border-gray-200 dark:border-gray-700 transition-colors">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AnimatedLogoRolodex size="small" />
              <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 transition-colors" style={{ fontFamily: 'Space Grotesk' }} data-testid="page-title">My Projects</h1>
            </div>
            <Button
              onClick={handleOpenCreateModal}
              className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600"
              data-testid="new-project-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Archive Toggle & Filters */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
          <Button
            onClick={() => setShowArchived(!showArchived)}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            {showArchived ? (
              <>
                <RotateCcw className="w-4 h-4" />
                Show Active Projects
              </>
            ) : (
              <>
                <Archive className="w-4 h-4" />
                Show Archived ({archivedProjects.length})
              </>
            )}
          </Button>
          {!showArchived && archivedProjects.length > 0 && (
              <span className="text-sm text-gray-500 dark:text-gray-400 transition-colors">
              {archivedProjects.length} archived project{archivedProjects.length !== 1 ? 's' : ''}
            </span>
          )}
          </div>
          <div className="flex items-center gap-3">
            <Switch
              id="budget-filter"
              checked={showBudgetOnly}
              onCheckedChange={setShowBudgetOnly}
            />
            <Label htmlFor="budget-filter" className="text-sm text-gray-600 dark:text-gray-400 transition-colors">
              Show only projects with budgets
            </Label>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredProjects.length === 0 ? (
          <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-0 dark:border-gray-700 transition-colors" data-testid="empty-projects">
            <div className="p-12 text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-cyan-400 to-teal-500 flex items-center justify-center">
                {showArchived ? <Archive className="w-10 h-10 text-white" /> : <FolderOpen className="w-10 h-10 text-white" />}
              </div>
              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-2 transition-colors" style={{ fontFamily: 'Space Grotesk' }}>
                {showBudgetOnly ? 'No projects with budgets yet' : showArchived ? 'No archived projects' : 'No projects yet'}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4 transition-colors">
                {showBudgetOnly
                  ? 'Add a budget to any project to track progress here.'
                  : showArchived
                  ? 'Archive completed projects to keep your workspace organized' 
                  : 'Create your first project to organize your time entries'
                }
              </p>
              {!showArchived && !showBudgetOnly && (
                <Button onClick={handleOpenCreateModal} className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600">
                  Create Project
                </Button>
              )}
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredProjects.map((project) => mapProjectToCard(project, showArchived))}
          </div>
        )}
      </div>

      {/* Create/Edit Project Modal */}
      <Dialog
        open={modalOpen}
        onOpenChange={(open) => {
          setModalOpen(open);
          if (!open) {
            setTimelineFocus(false);
            setBudgetFocus(false);
          }
        }}
      >
        <DialogContent data-testid="create-project-modal" className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 transition-colors">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-gray-100 transition-colors">{editingProject ? 'Edit Project' : 'Create New Project'}</DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400 transition-colors">
              {editingProject ? 'Update project details, budget, and hourly rate' : 'Add a project to organize your time entries'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="project-name" className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 block transition-colors">Project Name</Label>
              <Input
                id="project-name"
                placeholder="e.g., Client Website"
                value={projectForm.name}
                onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                className="h-11 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-cyan-500 dark:focus:border-cyan-400 transition-colors font-medium"
                data-testid="project-name-input"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 block transition-colors">Project Color</Label>
              <div className="grid grid-cols-5 gap-2 mt-2">
                {PRESET_COLORS.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setProjectForm({ ...projectForm, color })}
                    className={`w-12 h-12 rounded-lg transition-all ${
                      projectForm.color === color ? 'ring-4 ring-offset-2 ring-cyan-500 dark:ring-cyan-400 scale-110' : 'hover:scale-110'
                    }`}
                    style={{ backgroundColor: color }}
                    data-testid={`color-${color}`}
                  />
                ))}
              </div>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors">
              <input
                type="checkbox"
                id="billable"
                checked={projectForm.is_billable}
                onChange={(e) => setProjectForm({ ...projectForm, is_billable: e.target.checked })}
                className="w-5 h-5 text-cyan-600 dark:text-cyan-500 rounded border-2 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:ring-offset-0 transition-colors cursor-pointer"
                data-testid="billable-checkbox"
              />
              <Label htmlFor="billable" className="cursor-pointer text-sm font-medium text-gray-800 dark:text-gray-200 transition-colors flex-1">This is a billable project</Label>
            </div>
            
            {/* Category Selection */}
            <div>
              <Label className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 block transition-colors">Category</Label>
              <ProjectCategorySelect
                category={projectForm.category}
                onChange={(cat) => setProjectForm({ ...projectForm, category: cat })}
              />
            </div>
            
            {/* Tags Input */}
            <div>
              <Label className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 block transition-colors">Tags</Label>
              <ProjectTagsInput
                tags={projectForm.tags}
                onChange={(tags) => setProjectForm({ ...projectForm, tags })}
              />
            </div>
            
            {/* Default Project Toggle */}
            {!editingProject?.archived && (
              <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors">
                <input
                  type="checkbox"
                  id="default-project"
                  checked={projectForm.is_default}
                  onChange={(e) => setProjectForm({ ...projectForm, is_default: e.target.checked })}
                  className="w-5 h-5 text-yellow-500 dark:text-yellow-400 rounded border-2 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-yellow-500 dark:focus:ring-yellow-400 focus:ring-offset-0 transition-colors cursor-pointer"
                  data-testid="default-project-checkbox"
                />
                <Label htmlFor="default-project" className="cursor-pointer flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-200 transition-colors flex-1">
                  <Star className="w-4 h-4 text-yellow-500 dark:text-yellow-400" />
                  Set as default project for quick logging
                </Label>
              </div>
            )}
            
            {/* Budget Form */}
            <ProjectBudgetForm
              budget={projectForm.budget}
              hourlyRate={projectForm.hourly_rate}
              onChange={handleBudgetChange}
              autoFocusBudget={budgetFocus}
            />
            
            {/* Timeline Form */}
            <ProjectTimelineForm
              timeline={projectForm.timeline}
              milestones={projectForm.milestones}
              onChange={handleTimelineChange}
              autoFocusTimeline={timelineFocus}
            />
            
            <Button
              onClick={handleCreateProject}
              className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
              data-testid="create-project-submit"
            >
              {editingProject ? 'Update Project' : 'Create Project'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <BottomNav active="projects" />
    </div>
  );
};

export default Projects;
